import { z } from "zod";

export const policyTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  link: z.string().url(),
});

export const policySeveritySchema = z.enum([
  "low",
  "medium",
  "high",
  "critical",
]);

export const sourceTypeSchema = z.enum(["aws", "gcp", "azure"]);

const TextBlockSchema = z.object({
  text: z.string(),
  textType: z.enum(["plain_text", "code"]),
});

const RemediationStepSchema = z.object({
  stepNumber: z.number().int().positive(),
  textBlocks: z.array(TextBlockSchema),
});

const RemediationOptionItemSchema = z.object({
  optionName: z.string(),
  remediationSteps: z.array(RemediationStepSchema),
});

export const RemediationOptionSchema = z.object({
  source: z.string(),
  options: z.array(RemediationOptionItemSchema),
  displayName: z.string().optional(),
});

export const policySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  rule_definition: z.object({
    processor: z.string().optional(),
    evaluationCriteria: z.string().optional(),
    query: z.string().optional(),
  }),
  severity: policySeveritySchema,
  category: z.string(),
  is_active: z.boolean(),
  metadata: z.object({
    tags: z.union([z.string(), z.array(z.string())]),
    remediation_options: z.array(RemediationOptionSchema),
    exploitation_path: z.string(),
    risk_scenario: z.string(),
  }),
  is_custom: z.boolean(),
  parent_policy_name: z.string().nullable(),
  version: z.number(),
  attribution_count: z.number(),
});

export const policiesResponseSchema = z.object({
  values: z.array(policySchema),
  nextToken: z.number().nullable(),
  totalCount: z.number(),
  tags: z.array(policyTagSchema),
});

export const policyTagResponseSchema = z.array(policyTagSchema);

export type PolicyTag = z.infer<typeof policyTagSchema>;
export type PolicyTagResponse = z.infer<typeof policyTagResponseSchema>;
export type RemediationOption = z.infer<typeof RemediationOptionSchema>;
export type RemediationOptionItem = z.infer<typeof RemediationOptionItemSchema>;
export type Policy = z.infer<typeof policySchema>;
export type PolicySeverity = z.infer<typeof policySeveritySchema>;
export type PoliciesResponse = z.infer<typeof policiesResponseSchema>;
export type PolicySource = z.infer<typeof sourceTypeSchema>;

const filterOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const filterFieldSchema = z.object({
  options: z.array(filterOptionSchema),
  static: z.boolean(),
});

export const policiesFilterOptionsSchema = z.object({
  severity: filterFieldSchema,
  enabled: filterFieldSchema,
  tags: filterFieldSchema,
});

export type PoliciesFilterOptions = z.infer<typeof policiesFilterOptionsSchema>;
export const createPolicyPayloadSchema = z.object({
  name: z.string(),
  description: z.string(),
  rule_definition: z.object({
    evaluationCriteria: z.string(),
  }),
  severity: policySeveritySchema,
  metadata: z.object({
    tags: z.array(z.string()),
  }),
  is_active: z.boolean(),
});

export type PolicyPayload = z.infer<typeof createPolicyPayloadSchema>;

const suggestionSchema = z.object({
  text: z.string(),
  kind: z.string(),
  detail: z.string(),
  insert_text: z.string(),
  is_indexed: z.boolean(),
  sort_order: z.number().int().positive(),
});

export const suggestionsResponseSchema = z.object({
  suggestions: z.array(suggestionSchema),
  context: z.string(),
});

export type Suggestion = z.infer<typeof suggestionSchema>;
export type SuggestionsResponse = z.infer<typeof suggestionsResponseSchema>;

const LicenseDisplayMetadataSchema = z.object({
  showPlanHighlightLabel: z.boolean(),
  planHighlightLabelValue: z.string(),
});

const PermissionSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().optional(),
  licenseDisplayMetadata: LicenseDisplayMetadataSchema.optional(),
});

export const PermissionsAPIResponseSchema = z.object({
  data: z.object({
    permissions: z.record(z.string(), PermissionSchema),
  }),
});

export type PermissionsAPIResponse = z.infer<
  typeof PermissionsAPIResponseSchema
>;
export type PermissionType = z.infer<typeof PermissionSchema>;

export const resourceSchema = z.object({
  id: z.number(),
  name: z.string(),
  display_name: z.string().optional(),
  type: z.string(),
  created_date: z.number(),
});

export const resourcesResponseSchema = z.object({
  total_count: z.number(),
  results: z.array(resourceSchema),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

export type Resource = z.infer<typeof resourceSchema>;
export type ResourcesResponse = z.infer<typeof resourcesResponseSchema>;
